import { useState } from 'react';
import { CalendarClock, Pencil, X } from 'lucide-react';
import type { EarlyDeparturePlanSummary } from '@/shared/api';
import { Button, ConfirmDialog, Pill } from '@/shared/components';
import { formatDate } from '@/shared/lib/format';
import { earlyDeparturePlanErrorText, earlyDeparturePlanLabel } from './earlyDeparturePresentation';

type Props = {
  plan: EarlyDeparturePlanSummary;
  onEdit: () => void;
  onCancel: () => void;
  pending?: boolean;
};

export function EarlyDeparturePlanCard({ plan, onEdit, onCancel, pending = false }: Props) {
  const editable = plan.status === 'Scheduled' || plan.status === 'NeedsAttention';
  const problem = earlyDeparturePlanErrorText(plan.lastErrorCode);
  // Aufheben wirkt sofort auf Belegung und Kanal – deshalb erst nach Rückfrage.
  const [confirming, setConfirming] = useState(false);
  return (
    <section className="bg-amber-tint rounded-tile w-full p-4" aria-label="Geplante Abreise">
      <div className="flex items-start gap-3">
        <CalendarClock className="text-amber mt-0.5 shrink-0" size={18} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-ink font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
              Geplante Abreise
            </p>
            <Pill variant={plan.status === 'NeedsAttention' ? 'stop' : 'warning'} size="sm">
              {earlyDeparturePlanLabel(plan.status)}
            </Pill>
          </div>
          <p className="text-ink mt-2 font-serif text-xl">
            {formatDate(plan.plannedDepartureDate)}
          </p>
          <p className="text-ink-soft mt-1 text-xs">
            Bis zum Vollzug bleibt der Aufenthalt belegt.
          </p>
          {problem && <p className="text-sealwax mt-2 text-xs">{problem}</p>}
        </div>
      </div>
      {editable && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="min-h-11"
            variant="primary"
            disabled={pending}
            leftIcon={<Pencil size={14} />}
            onClick={onEdit}
          >
            Ändern
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pending}
            className="text-sealwax hover:bg-sealwax/10 min-h-11"
            leftIcon={<X size={14} />}
            onClick={() => setConfirming(true)}
          >
            Plan aufheben
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Geplante Abreise aufheben?"
        description={`Die für ${formatDate(plan.plannedDepartureDate)} geplante Abreise wird nicht ausgeführt. Der Aufenthalt bleibt bis zum vertraglichen Abreisetag belegt.`}
        confirmLabel="Plan aufheben"
        cancelLabel="Plan behalten"
        onConfirm={() => {
          setConfirming(false);
          onCancel();
        }}
      />
    </section>
  );
}
